/**
 * ProductDetailContainer
 * --
 */
import React, { useState, useRef } from 'react';
import ProductDetailPresenter from './ProductDetailPresenter';
import { useParams } from 'react-router-dom';

import { useGetProduct } from 'hooks/ProductHooks';
import { decrypt, encrypt } from 'utils/Crypto';
import { useGetInquiries } from 'hooks/InquiryHooks';
import { useGetReviews } from 'hooks/ReviewHooks';
import {
  extractApplicationPolicy,
  STORE_URL,
  stringToMoneyFormat,
  DOMAIN_URL,
  DEPLOY_TYPE,
  calculateRentalMonthlyPayment,
  findGetRootData,
  extractApplicationCommission,
  openPopup,
  isMobile,
} from 'utils';
import lodash from 'utils/Lodash';
import MessageAlert from 'utils';
import dayjs from 'dayjs';

import { useUpsertBulkCart } from 'hooks/CartHooks';
import { useCreateWishlist, useDeleteWishlist } from 'hooks/WishlistHooks';
import {
  useDefaultShopDataStack,
  useUserDataAction,
  useUserDataStack,
  useUserStack,
} from 'store';
import { CartApi } from 'api';
import { useCreateInquiry, useDeleteInquiry } from 'hooks/InquiryHooks';
import { useBulkCreateInquiryFile } from 'hooks/InquiryFileHooks';
import { FileManager } from 'utils/FileManager';
import { useQueryClient } from '@tanstack/react-query';
import { inquiryQueryKeys } from 'hooks/InquiryHooks';
import { useGetMapProductCoupons } from 'hooks/CouponHooks';
import { useRegisterCouponUser } from 'hooks/CouponUserHooks';
import { useGetForwardingLocations } from 'hooks/ForwardingLocationsHooks';
import CONSTANTS from 'constants';
import calculateApplyPolicy from 'utils/productPolicyUtils';
import Calculate from 'utils/Calculate';

const MAX_FILE_COUNT = 5;
const MAX_IMAGE_SIZE = 1 * 1024 * 1024;

const DEFAULT_GRADE_BENEFIT = {
  discount_rate: 0,
  discount_type: 'R',
  reserve_rate: 0,
  reserve_type: 'R',
};

const ProductDetailContainer = (props) => {
  /* ===== ROUTE ===== */
  const { navigate, location, screenSize } = props;
  const { productId } = useParams();

  // 상품 idx
  const ProductIdx = React.useMemo(() => {
    const decodeIdx = decrypt(productId);
    return Number(decodeIdx);
  }, [productId]);

  /* ===== Mutate ===== */
  // 장바구니
  const { upsertBulkCart } = useUpsertBulkCart();
  // 위시리스트
  const { createWishlist } = useCreateWishlist();
  const { deleteWishlist } = useDeleteWishlist();
  // 문의
  const { createInquiry } = useCreateInquiry();
  const { deleteInquiry } = useDeleteInquiry();
  const { bulkCreateInquiryFile } = useBulkCreateInquiryFile();
  // 쿠폰 발급
  const { registerCouponUser } = useRegisterCouponUser();

  /* ===== Data ===== */
  const queryClient = useQueryClient();

  // NOTE: 상품 상세 조회
  const { data: Product } = useGetProduct(ProductIdx, null, {
    enabled: !!ProductIdx,
    select: (res) => {
      if (res.status !== 200) return null;

      return res.data;
    },
  });

  // NOTE: 상품 문의 내역
  const { data: Inquiries } = useGetInquiries(
    {
      product_id: ProductIdx,
      join_tables: '["inquiry_file", "user", "shop_information"]',
    },
    {
      enabled: !!ProductIdx,
      initialData: [],
      select: (res) => {
        if (res.status !== 200) return [];

        return res.data;
      },
    },
  );
  // NOTE: 리뷰 내역 조회
  const { data: Reviews } = useGetReviews(
    {
      product_id: ProductIdx,
      join_tables: '["review_file", "user", "parcel_service", "rental"]',
      is_show: 'Y',
    },
    {
      enabled: !!ProductIdx,
      initialData: [],
      select: (res) => {
        if (res.status !== 200) return [];

        return res.data;
      },
    },
  );
  // NOTE: 다운로드 가능 쿠폰 조회
  const { data: Coupons } = useGetMapProductCoupons(
    {
      payment_type: 'DOWNLOAD',
    },
    {
      enabled: !!ProductIdx,
      initialData: [],
      select: (res) => {
        if (res.status !== 200) return [];

        return res.data;
      },
    },
  );
  // NOTE: 출고지(픽업지)
  const { data: Forwarding } = useGetForwardingLocations(
    {
      shop_information_id: Product?.shop_information_id,
    },
    {
      enabled: !!Product,
      initialData: [],
      select: (res) => {
        if (res.status !== 200) return [];

        // 픽업지만 추출
        const receive = res.data.filter(
          (forwarding) => forwarding.type === 'RECEIVE',
        );

        return receive;
      },
    },
  );

  /* ===== Store ===== */
  const userInfo = useUserStack();
  const { cartList, wishlist, couponList } = useUserDataStack();
  const userDataAction = useUserDataAction();
  const { promotion, categories } = useDefaultShopDataStack();

  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  const layoutRef = useRef(null);
  const contentRef = useRef(null);
  // 선택 상품
  const [selectedProduct, setSelectedProduct] = useState([]);
  // 장바구니 담을 옵션
  const [selectedOption, setSelectedOption] = React.useState(null);
  // 상품 정보 더 보기
  const [isMore, setIsMore] = useState(false);
  // 배송주기
  const [regularCycle, setRegularCycle] = React.useState({
    regular_cycle: null,
    max_regular_delivery_count: null,
  });

  // Tab Key
  const [tabKey, setTabKey] = useState('content');

  // 문의하기 입력값
  const [inputValue, setInputValue] = React.useState({
    type: '',
    name: '',
    content: '',
    secret: false,
  });
  const [files, setFiles] = React.useState([]);
  // 로딩
  const [isLoading, setIsLoading] = React.useState(false);
  // 선택 렌탈 옵션
  const [selectedRentalOption, setSelectedRentalOption] = React.useState(null);

  // 웹뷰, 웹앱 여부
  const [agentType, setAgentType] = React.useState('PC');

  /* ===== VARIABLE ===== */

  /* ===== FUNCTION ===== */
  /**
   * Tab Change
   * --
   * @param {string} k
   */
  const handleTabChange = (k) => {
    setTabKey(k);

    // contentRef[k].scrollIntoView({
    //   behavior: 'smooth', // 부드러운 스크롤
    //   block: 'nearest', // 스크롤 시 요소가 상단에 배치되도록 설정
    // });

    const offsetTop =
      contentRef[k].getBoundingClientRect().top + window.scrollY;

    // 반응형 여유 높이 설정
    const offsetAdjustment =
      screenSize <= 768 ? (agentType === 'WEBVIEW' ? 130 : 190) : 230;

    // 스크롤 이동
    window.scrollTo({
      top: offsetTop - offsetAdjustment,
      behavior: 'smooth', // 부드러운 스크롤
    });
  };

  /**
   * 옵션 합산 금액 계산
   * --
   * @param {array | null} product_option
   * @returns
   */
  const calculateTotalOptionPrice = (product_option) => {
    if (!product_option || !Array.isArray(product_option)) return 0;

    return product_option.reduce((optionAcc, optionCur) => {
      const { option } = optionCur;

      // option이 없거나 배열이 아니면 기본값 반환
      if (!option || !Array.isArray(option)) return optionAcc;

      const optionPriceSum = option.reduce((childAcc, childCur) => {
        return childAcc + (childCur.option_price || 0);
      }, 0);

      return optionAcc + optionPriceSum;
    }, 0);
  };

  /**
   * 상품 목록 핸들러
   * --
   * @param {string} type
   * @param {number} index
   * @param {object | number} value
   */
  const handleProductList = (type = 'UPDATE', index, value) => {
    switch (type) {
      case 'UPDATE':
        const updateData = {
          ...selectedProduct[index],
          cartNum: value,
        };

        setSelectedProduct((prev) => [
          ...prev.slice(0, index),
          updateData,
          ...prev.slice(index + 1),
        ]);
        break;
      case 'ADD':
        // 추가할 데이터
        let addData = {
          ...value,
        };

        if (selectedOption) {
          // 필수 옵션 모두 선택한지 체크
          // 선택되지 않은 옵션 반환
          const notSelectedOptions = ProductOptions.REQUIRED.filter(
            (option) =>
              !selectedOption.some(
                (select) => select.option_id.toString() === option.key,
              ),
          );

          if (notSelectedOptions.length) {
            MessageAlert.warning('필수 옵션을 모두 선택해주세요!');
            return;
          }

          // 순서대로 들어가도록 정렬
          const sortOption = [...selectedOption].sort(
            (a, b) => a.option_id - b.option_id,
          );

          addData = {
            ...value,
            product_option: sortOption.map((option) => ({
              ...option,
              option: [option.option],
            })),
          };

          const isExist = selectedProduct.some(
            (product) =>
              lodash.isEqual(product.product_option, addData.product_option) &&
              product.regular_cycle === addData.regular_cycle,
          );

          if (isExist) {
            MessageAlert.warning('이미 같은 옵션의 상품이 존재합니다');
            return;
          }

          setSelectedOption([]);
        }

        // NOTE: 정기배송 상품일 경우 유효성 검사 추가
        if (addData.type === 'SUBSCRIPTION') {
          // 배송 주기를 선택하지 않았을 경우
          if (!addData.regular_cycle) {
            MessageAlert.warning('배송주기를 선택해주세요!');
            return;
          }

          // 배송 횟수를 선택하지 않았을 경우
          if (
            !addData.max_regular_delivery_count &&
            addData.max_regular_delivery_count !== 0
          ) {
            MessageAlert.warning('배송횟수를 선택해주세요!');
            return;
          }

          const isExist = selectedProduct.some(
            (product) =>
              product.regular_cycle === addData.regular_cycle &&
              product.max_regular_delivery_count ===
                addData.max_regular_delivery_count,
          );

          if (isExist) {
            MessageAlert.warning('이미 같은 옵션의 상품이 존재합니다');
            return;
          }
        }

        setRegularCycle({
          regular_cycle: null,
          max_regular_delivery_count: null,
        });
        setSelectedProduct((prev) => [...prev, addData]);
        break;
      case 'REMOVE':
        setSelectedProduct((prev) => prev.filter((_, i) => i !== index));
        break;
      default:
        return;
    }
  };

  /**
   * 장바구니/위시리스트 추가
   * --
   * @param {string} t
   * @param {number | object} d
   * @returns
   */
  const handleSubmit = async (t = 'cart', idx = null) => {
    const confirm = (msg = '') => window.confirm(msg);

    if (!userInfo) {
      if (confirm('로그인 후 이용가능합니다.\n로그인하시겠습니까?')) {
        return navigate('signin', {
          state: {
            from: `${location.pathname}${
              location.search ? location.search : ''
            }`,
          },
        });
      } else {
        return;
      }
    }
    if (ProductData?.product?.product_status !== 'NORMAL') {
      MessageAlert.warning('구매할 수 없는 상품입니다.');
      return;
    }
    if (
      ProductData?.product?.isBusinessCategory &&
      userInfo?.business_level !== 2
    ) {
      MessageAlert.warning('사업자 회원만 구매 가능한 상품입니다!');
      return;
    }

    let mutateData = {};
    switch (t) {
      case 'cart': // 장바구니 추가
        if (!selectedProduct.length) {
          MessageAlert.warning('구매하실 상품을 선택해주세요!');
          return false;
        }

        // 같은 상품 id를 가진 장바구니 목록 정제
        const filterSameProduct = cartList.filter(
          (cart) => Number(cart.product_id) === Number(ProductIdx),
        );

        mutateData = selectedProduct.map((product) => {
          // 옵션이 같고 배송주기가 같은 상품 찾기
          const findData =
            filterSameProduct.find((cart) => {
              // product_option 비교
              const hasProductOption =
                product.product_option && product.product_option.length;
              const isOptionEqual = hasProductOption
                ? lodash.isEqual(cart.product_option, product.product_option)
                : true;

              // regular_cycle 비교 (존재 여부와 값 비교)
              const isRegularCycleEqual = product.regular_cycle
                ? product.regular_cycle === cart.regular_cycle
                : true;

              // 출고지 비교
              const isForwarding = cart?.forwarding_location_id === idx;

              return isOptionEqual && isRegularCycleEqual && isForwarding;
            }) ?? null;

          return {
            user_id: userInfo.user_id,
            product_id: product.product_id,
            cart_num: findData
              ? findData.cart_num + product.cartNum
              : product.cartNum,
            shop_information_id: product.shop_information_id,
            product_option: product.product_option,
            cart_id: findData ? findData.cart_id : null,
            cart_type: !idx ? product.type : 'STORE_PICKUP',
            regular_cycle: product.regular_cycle,
            max_regular_delivery_count: product.max_regular_delivery_count,
            forwarding_location_id: idx,
          };
        });

        await handleMutate(upsertBulkCart, mutateData)
          .then(async (res) => {
            if (res.status !== 200) {
              MessageAlert.warning('장바구니 추가에 실패하였습니다.');
              return;
            }
            MessageAlert.success('장바구니에 추가되었습니다.');

            // 장바구니 Store 갱신
            const { data } = await CartApi.getCarts({
              user_id: userInfo.user_id,
            });
            userDataAction.setData('cartList', data);
          })
          .catch(() => {
            MessageAlert.error('장바구니 추가에 실패하였습니다.');
            return false;
          });

        return true;

      case 'wishlist': // 위시리스트
        mutateData = {
          user_id: userInfo.user_id,
          product_id: ProductIdx,
        };
        await handleMutate(createWishlist, mutateData).then((res) => {
          if (res.status !== 200) {
            MessageAlert.warning('찜목록에 추가에 실패하였습니다.');
            return;
          }
          MessageAlert.success('찜목록에 추가되었습니다.');
          userDataAction.addData('wishlist', res.data);
        });
        return;

      case 'remove': // 장바구니 제거
        mutateData = wishlist.find(
          (wish) => wish.product_id === ProductIdx && wish.type === 'WISH',
        );

        await handleMutate(deleteWishlist, mutateData.wishlist_id).then(
          (res) => {
            if (res.status !== 200) {
              MessageAlert.warning('찜목록에서 삭제하는데 실패하였습니다.');
              return;
            }
            MessageAlert.success('찜목록에서 삭제되었습니다.');
            userDataAction.removeData('wishlist', 'wishlist_id', mutateData);
          },
        );
        return;

      case 'subscribe': // 재입고 알림
        if (!confirm('재입고 알림신청 하시겠습니까?')) return;

        mutateData = {
          user_id: userInfo.user_id,
          product_id: ProductIdx,
          type: 'RESTOCK',
        };

        await handleMutate(createWishlist, mutateData).then((res) => {
          if (res.status !== 200) {
            MessageAlert.warning('재입고 알림신청에 실패하였습니다.');
            return;
          }

          MessageAlert.success('재입고 알림신청이 완료되었습니다.');
          userDataAction.addData('wishlist', res.data);
        });

        return;

      case 'unsubscribe': // 재입고 알림 해지
        if (!confirm('재입고 알림신청을 해지 하시겠습니까?')) return;

        mutateData = mutateData = wishlist.find(
          (wish) => wish.product_id === ProductIdx && wish.type === 'RESTOCK',
        );

        await handleMutate(deleteWishlist, mutateData.wishlist_id).then(
          (res) => {
            if (res.status !== 200) {
              MessageAlert.warning('재입고 알림신청 해지에 실패하였습니다.');
              return;
            }
            MessageAlert.success('재입고 알림신청이 해지되었습니다.');
            userDataAction.removeData('wishlist', 'wishlist_id', mutateData);
          },
        );

        return;

      case 'rental': //렌탈
        if (!confirm('선택하신 옵션으로 렌탈신청 하시겠습니까?')) return;

        const rentalItems = selectedProduct.map((product) => {
          const {
            shop_information,
            isSoldOut,
            product_option,
            forwarding_location,

            // ===== 불필요 데이터 ===== //
            rental_information,
            default_rental_option,
            cartNum,
            regular_information,
            product_content,
            monthlyPrice,
            finalMonthly,
            pickup,
            files,
            // ===== 불필요 데이터 ===== //

            // policies,
            ...rest
          } = product;

          const optionPrice = calculateTotalOptionPrice(product_option);

          return {
            user_id: userInfo?.user_id,
            options: ProductData?.product?.product_option ?? null,
            ...rest,
            product_option,
            brand: shop_information,
            // policy: policies,
            option_price: optionPrice,
            is_sold_out: isSoldOut,
            forwarding_location,
            rental_option: selectedRentalOption,
            cart_num: 1,
            advance_price: selectedRentalOption?.advance_price?.option_price,
            contract_date: selectedRentalOption?.contract_date?.option_value,
            additional_cost:
              selectedRentalOption?.additional_cost?.option_price,
            mandatory_usage_date:
              selectedRentalOption?.contract_date?.mandatory_usage_date
                ?.option_value,
          };
        });

        userDataAction.setData('shopOrderList', rentalItems);

        navigate(`/order/order?type=rental`);
        return;

      case 'present': // 선물하기
        if (isMobile()) {
          navigate(
            `/common/present?pid=${encodeURIComponent(
              encrypt(idx),
            )}&type=product`,
          );
        } else {
          openPopup(
            `/common/present?pid=${encodeURIComponent(
              encrypt(idx),
            )}&type=product`,
          );
        }

        return;

      case 'naver_pay':
      case 'kakao_pay':
        const productItems = selectedProduct.map((product) => {
          const {
            shop_information,
            isSoldOut,
            product_option,
            forwarding_location,

            // ===== 불필요 데이터 ===== //
            rental_information,
            default_rental_option,
            cartNum,
            regular_information,
            product_content,
            monthlyPrice,
            finalMonthly,
            pickup,
            files,
            // ===== 불필요 데이터 ===== //

            policies,
            ...rest
          } = product;

          const optionPrice = calculateTotalOptionPrice(product_option);

          return {
            user_id: userInfo?.user_id,
            options: ProductData?.product?.product_option ?? null,
            ...rest,
            product_option,
            brand: shop_information,
            policy: policies,
            option_price: optionPrice,
            is_sold_out: isSoldOut,
            forwarding_location,
            cart_num: 1,
          };
        });

        userDataAction.setData('shopOrderList', productItems);

        navigate(`/order/order?type=delivery&payment=${t}`);

        return;

      default:
        MessageAlert.warning(
          `${t === 'cart' ? '장바구니 추가' : '찜하기'}에 실패하였습니다`,
        );
        return;
    }
  };

  /**
   * Mutate Function 실행
   * --
   * @param {function} mutateFn
   * @param {object} mutate
   * @returns
   */
  const handleMutate = async (mutateFn, mutate) => {
    try {
      const res = await mutateFn(mutate);

      return res;
    } catch (err) {
      console.error('[Product handleMutate] Error !! ', err);
      // MessageAlert.error(
      //   `${t === 'cart' ? '장바구니 추가' : '찜하기'}에 실패하였습니다`
      // );
      throw err;
    }
  };

  /**
   * 옵션 선택 핸들러
   * --
   */
  const handleOptionSelect = (target, optionKey) => {
    const [parentKey, childKey] = optionKey.split('-'); // 부모와 자식 키 분리

    // 부모 옵션 찾기
    const parentOption = ProductOptions[target].find(
      (option) => option.key === parentKey,
    );
    if (!parentOption) {
      console.error(`Parent option with key ${parentKey} not found.`);
      return;
    }

    // 자식 옵션 찾기 및 정제
    const childOption = parentOption.origin.option.find(
      (child) => child.option_key.toString() === childKey,
    );
    if (!childOption) {
      console.error(
        `Child option with key ${childKey} not found in parent ${parentKey}.`,
      );
      return;
    }

    const selectedChildOption = {
      ...parentOption.origin,
      option: childOption,
    };

    // 이미 선택된 옵션인지 확인
    const existingIndex = selectedOption.findIndex(
      (option) => option.option_id === selectedChildOption.option_id,
    );

    // 상태 업데이트
    setSelectedOption((prev) => {
      if (existingIndex === -1) {
        // 새로운 옵션 추가
        return [...prev, selectedChildOption];
      }

      // 동일한 옵션일 경우 상태 업데이트 생략
      if (prev[existingIndex].option === selectedChildOption.option) {
        return prev;
      }

      // 기존 옵션 업데이트
      const updatedOptions = [...prev];
      updatedOptions[existingIndex] = selectedChildOption;
      return updatedOptions;
    });
  };

  /**
   * 데이터 바인딩 함수
   * --
   * @param {string} target
   * @param {string} value
   * @param {func} callback
   */
  const handleChange = (t, v, callback = null) => {
    let newData = inputValue;

    newData[t] = v;
    const d = {
      ...newData,
    };

    setInputValue(d);

    callback && callback();
  };

  /**
   * 파일 리스트 핸들러
   * --
   * @param {string} type
   * @param {object | number} file
   * @returns
   */
  const handleFileList = (type = 'add', file) => {
    const handlerType = type.toLocaleUpperCase();

    switch (handlerType) {
      case 'ADD':
        const list = Array.from(file);
        if (list.length + files.length > MAX_FILE_COUNT) {
          MessageAlert.warning(
            `파일은 최대 ${MAX_FILE_COUNT}개의 파일만 업로드할 수 있습니다.`,
          );
          return;
        }

        // 파일명이 50자 이상 있는지 체크
        const fileNameLength = list.some((file) => file.name.length > 50);
        if (fileNameLength) {
          MessageAlert.info(
            `파일명은 50자를 초과할 수 없습니다. 파일명을 수정해주세요.`,
          );
          return;
        }

        // 파일 크기 검사
        const filterFiles = list.filter((file) => file.size <= MAX_IMAGE_SIZE);

        if (filterFiles.length !== list.length) {
          MessageAlert.info(
            `이미지는 최대 1MB, 동영상은 최대 5MB까지 등록 가능합니다`,
          );
        }

        setFiles((prev) => [...prev, ...filterFiles]);
        return;
      case 'REMOVE':
        setFiles((prev) => [...prev.slice(0, file), ...prev.slice(file + 1)]);
        return;
      default:
        return;
    }
  };

  /**
   * 문의하기
   * --
   */
  const handleReceptionInquiry = async (callback) => {
    const { name, content, type } = inputValue;

    if (!name) {
      MessageAlert.info('문의 제목을 입력해주세요!');
      return;
    }

    if (!content) {
      MessageAlert.info('문의할 내용을 입력해주세요!');
      return;
    }

    if (!type) {
      MessageAlert.info('문의유형을 선택해주세요!');
      return;
    }

    try {
      setIsLoading(true);

      const inquiryData = {
        user_id: userInfo.user_id,
        shop_information_id: ProductData?.product?.shop_information_id, // NOTE: 1:1 문의하기일 경우 JM(총관리자)으로 고정
        inquiry_name: name,
        inquiry_content: content,
        inquiry_type: 'PRODUCT',
        product_id: ProductIdx,
        is_secret: 'Y',
        inquiry_sub_type: type,
      };

      // NOTE: 문의 생성
      const { status, data } = await createInquiry(inquiryData);

      let inquiryStoreData = {
        ...data,
        review_file: [],
      };

      if (status !== 200) {
        MessageAlert.warning('문의하기에 실패하였습니다.');
        return;
      }

      // NOTE: 파일 등록 처리
      if (files.length) {
        // NOTE: 파일 멀티 업로드
        const upload = await FileManager.multiUpload(
          `/inquiry/${data.inquiry_id}`,
          files,
        );

        // NOTE: 실패시 문의 삭제
        if (upload.status !== 200) {
          await deleteInquiry(data.inquiry_id);
          console.error('파일 업로드 실패!', upload);
          MessageAlert.warning('문의하기에 실패하였습니다.');
          return;
        }

        const createFileData = upload.data.map((file) => ({
          ...file,
          inquiry_id: data.inquiry_id,
          type: 'IMAGE',
        }));

        const res = await bulkCreateInquiryFile(createFileData);

        if (res.status !== 200) {
          await deleteInquiry(data.inquiry_id);
          console.error('파일 업로드 실패!', upload);
          MessageAlert.warning('문의하기에 실패하였습니다.');
          return;
        }

        inquiryStoreData = {
          ...inquiryStoreData,
          inquiry_file: res.data,
        };
      }

      userDataAction.addData('inquiryList', inquiryStoreData);

      // NOTE: 입력값 초기화
      setInputValue({
        type: '',
        name: '',
        content: '',
        secret: false,
      });
      setFiles([]);

      MessageAlert.success('1:1문의가 성공적으로 접수되었습니다!');
      setTimeout(() => callback(), 500);

      // NOTE: 데이터 최신화
      const queryKey = inquiryQueryKeys.getInquiries({
        product_id: ProductIdx,
        join_tables: '["inquiry_file", "user", "shop_information"]',
      });
      queryClient.invalidateQueries({
        queryKey: queryKey.queryKey,
      });
    } catch (err) {
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    }
  };

  /**
   * 링크 클립보드 복사
   * --
   */
  const handleCopyUrl = async () => {
    try {
      const port = DEPLOY_TYPE === 'local' ? ':3000' : '';
      const protocol = window.location.protocol;
      const sub =
        DEPLOY_TYPE === 'development'
          ? 'mall-jm-coffee.'
          : DEPLOY_TYPE === 'local'
            ? ''
            : 'mall.';

      const url = `${protocol}//${sub}${DOMAIN_URL}${port}/#`;
      const pid = encodeURIComponent(encrypt(ProductIdx));

      const productLink = `${url}/products/${pid}`;

      const clipboard = navigator.clipboard;

      if (clipboard) {
        await navigator.clipboard.writeText(productLink);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = productLink;
        document.body.appendChild(textArea);
        textArea.select();
        textArea.setSelectionRange(0, 99999);

        document.execCommand('copy');

        textArea.setSelectionRange(0, 0);
        document.body.removeChild(textArea);
      }
      MessageAlert.success('복사되었습니다.');
    } catch (err) {
      console.error('[handleCopyUrl] Error!! ', err);
      MessageAlert.error('링크 복사에 실패하였습니다!');
    }
  };

  /**
   * 쿠폰 다운로드 발급
   * --
   * @param {string} code
   */
  const handleCouponDownload = async (code) => {
    if (!userInfo) {
      const confirm = window.confirm(
        '로그인 후 이용가능합니다.\n로그인하시겠습니까?',
      );

      if (confirm) {
        return navigate('signin', {
          state: {
            from: `${location.pathname}${
              location.search ? location.search : ''
            }`,
          },
        });
      } else {
        return;
      }
    }

    try {
      setIsLoading(true);

      const { status, data, message } = await registerCouponUser({
        user_id: userInfo.user_id,
        coupon_code: code,
      });

      if (status !== 200) {
        if (status === 422) {
          MessageAlert.info(message);
          return;
        }

        MessageAlert.warning('쿠폰 다운로드에 실패하였습니다');
        return;
      }

      userDataAction.addData('couponList', data);
      MessageAlert.success('쿠폰이 성공적으로 발급되었습니다.');
    } catch (err) {
      console.error('[handleCouponDownload] Error!! : ', err);
      MessageAlert.error('쿠폰 다운로드에 실패하였습니다.');
    } finally {
      setTimeout(() => setIsLoading(false), 500);
    }
  };

  /**
   * 렌탈 디폴트 옵션 추출
   * --
   * 재귀적으로 JSON 데이터를 순회하면서,
   * 배열 내 객체에 `default` 프로퍼티가 있는 경우 true인 항목만 남깁니다.
   */
  const extractDefaultSelections = (data) => {
    if (Array.isArray(data)) {
      // 배열 내에서 default: true인 첫 번째 객체 선택
      const defaultItem = data.find(
        (item) => item && typeof item === 'object' && item.default === true,
      );

      if (!defaultItem) return null; // default: true가 없으면 null 반환

      // default로 선택된 객체를 재귀적으로 처리 (내부 배열 포함)
      return extractDefaultSelections({ ...defaultItem });
    } else if (data !== null && typeof data === 'object') {
      const result = {};
      for (const key in data) {
        if (!Object.prototype.hasOwnProperty.call(data, key)) continue;
        if (key.endsWith('_description')) continue; // 설명 필드 제거

        // 배열이면 default 값이 있는 객체를 찾아 적용 (내부 배열도 재귀적으로 처리)
        if (Array.isArray(data[key])) {
          const selected = extractDefaultSelections(data[key]);
          if (selected !== null) result[key] = selected; // 선택된 값이 있다면 유지
        } else {
          result[key] = extractDefaultSelections(data[key]); // 재귀 처리
        }
      }
      return result;
    }
    return data;
  };

  /**
   * 렌탈 옵션 핸들러
   * --
   * @param {object} option 렌탈 선택 옵션
   * @returns
   */
  const handleSelectRentalOption = (option) => {
    if (!option) return;

    const { key } = option;

    // 선택 렌탈 옵션 복사
    const selected = { ...selectedRentalOption };

    switch (key) {
      case 'contract_date':
        const { mandatory_usage_date, ...rest } = option;

        // 선택된 옵션과 값이 다를 경우 mandatory_usage_date 0번째 값으로 변경
        if (selected['contract_date'].option_key !== rest?.option_key) {
          selected['contract_date'] = { ...rest };
          selected['contract_date']['mandatory_usage_date'] =
            mandatory_usage_date?.[0];
        }
        break;
      case 'mandatory_usage_date':
        selected['contract_date']['mandatory_usage_date'] = option;
        break;
      default:
        selected[key] = option;
        break;
    }

    setSelectedRentalOption(selected);
  };

  /* ===== HOOKS ===== */
  /**
   * 상품 데이터 정제
   * --
   */
  const ProductData = React.useMemo(() => {
    if (!Product) return { product: null, information: [] };

    const {
      product_file = [],
      product_policy = [],
      product_detail,
      num,
      product_status,
      receive_ids,
      type,
      rental_information,
      price,
      tag,
      grade_benefit_info,
      ...rest
    } = Product;
    // 상품 파일 추출
    const productFiles = product_file.length
      ? product_file?.reduce((acc, file) => {
          const { type, file_url } = file;

          if (type === 'PRODUCT') acc.push(`${STORE_URL}/${file_url}`);

          return acc;
        }, [])
      : [];

    // 썸네일 추출
    const thumbnail =
      product_file.find((file) => file.type === 'THUMBNAIL') || null;

    // 적용 정책 추출
    const applicationPolicy = product_policy.length
      ? extractApplicationPolicy(product_policy, price, promotion)
      : null;
    // 적용 등급 할인율
    const gradeBenefit =
      grade_benefit_info && userInfo?.grade_id
        ? (grade_benefit_info[userInfo?.grade_id] ?? DEFAULT_GRADE_BENEFIT)
        : DEFAULT_GRADE_BENEFIT;

    const apply_grade_benefit = {
      policy_type: 'GRADE',
      ...gradeBenefit,
    };

    // 현재 진행중인 할인/적립 정책 적용
    const { apply_policy_info, final_discount_price, final_reserve_price } =
      calculateApplyPolicy(
        { price: price },
        apply_grade_benefit,
        applicationPolicy,
      );

    // 최상위 카테고리 추출
    const rootCategory = findGetRootData(categories, rest.product_category_id);

    // 적용 수수료
    const commission = product_policy
      .filter((policy) => {
        if (policy.policy_type === 'PROMOTION') {
          return promotion?.some(
            (pr) => pr.promotion_id === policy.promotion_id,
          );
        }

        return true;
      })
      .map((policy) => {
        const { commission_rate, policy_type } = policy;

        return {
          type: policy_type,
          commission_rate: commission_rate,
        };
      });
    const commissionList = [
      ...commission,
      {
        type: 'BRAND',
        commission_rate: rest?.shop_information?.commission_rate,
      },
      { type: 'CATEGORY', commission_rate: rootCategory?.commission_rate },
    ];
    // 적용된 수수료 추출
    const applyCommission = extractApplicationCommission(commissionList);

    // 렌탈 기본 옵션
    const rentalOptions =
      type === 'RENTAL' ? extractDefaultSelections(rental_information) : null;

    const newProduct = {
      ...rest,
      src: !thumbnail ? null : `${STORE_URL}/${thumbnail.file_url}`,
      num,
      product_status,
      type,
      rental_information,
      default_rental_option: rentalOptions,
      price: price,
      monthlyPrice:
        type === 'RENTAL'
          ? calculateRentalMonthlyPayment(price, rentalOptions)
          : 0,
      policy: applicationPolicy,
      discount_rate: Calculate.calculateDiscountRate(
        price,
        price - final_discount_price,
      ),
      discount_type: 'R',
      discount_price: final_discount_price,
      apply_discount_rate: Calculate.calculateDiscountRate(
        price,
        price - final_discount_price,
      ),
      reserve_rate: final_reserve_price,
      // reserve_type: applicationPolicy ? applicationPolicy.reserve_type : 'W',
      finalPrice: price - final_discount_price,
      finalMonthlyPrice:
        type === 'RENTAL'
          ? calculateRentalMonthlyPayment(
              price - final_discount_price,
              rentalOptions,
            )
          : 0,
      files: productFiles,
      isSoldOut: num === 0 || product_status === 'SOLDOUT' ? 'Y' : 'N',
      pickup:
        rest.is_store_pickup === 'Y' && receive_ids?.length
          ? receive_ids?.map((receive) => {
              const forwarding = Forwarding.find(
                (_item) =>
                  _item.forwarding_location_id ===
                  receive.forwarding_location_id,
              );

              return {
                ...receive,
                ...forwarding,
              };
            })
          : [],

      policies: apply_policy_info,
      commission: applyCommission,
      tag: tag?.filter((_item) => _item?.is_show === 'Y'),
      isBusinessCategory:
        rootCategory?.product_category_name ===
        CONSTANTS.BUSINESS_CATEGORY_NAME,
      apply_grade_benefit,
      apply_policy: applicationPolicy,
    };

    // 상품상세정보
    const newInformation = product_detail
      ? Object.entries(product_detail).map(([key, value]) => ({
          title: key,
          children: Object.entries(value).map(([subKey, subValue]) => ({
            title: subKey,
            value: subValue,
          })),
        }))
      : [];

    return {
      product: newProduct,
      information: newInformation,
    };
    // eslint-disable-next-line
  }, [Product, promotion, Forwarding, categories]);

  /**
   * 상품 문의하기
   * --
   */
  const InquiryList = React.useMemo(() => {
    if (!Inquiries.length) return [];

    const newInquiryList = Inquiries.map((inquiry) => {
      const {
        user,
        inquiry_name,
        inquiry_content,
        inquiry_answer,
        created_at,
        updated_at,
        shop_information,
        user_id,
      } = inquiry;

      return {
        title: inquiry_name,
        content: inquiry_content,
        answer: inquiry_answer,
        userName: `${user.user_name[0]}**`,
        answerName: shop_information?.shop_information_name ?? '-',
        create: created_at,
        status: inquiry_answer ? 'DONE' : 'WAIT',
        secret: !userInfo || user_id !== userInfo?.user_id ? true : false,
        update: updated_at,
      };
    });

    const sortData = newInquiryList.sort((a, b) => b.create - a.create);

    return sortData;
  }, [Inquiries, userInfo]);

  /**
   * 상품 리뷰
   * --
   */
  const ReviewList = React.useMemo(() => {
    if (!Reviews.length) return { reviews: [], photo: [] };

    const reviewList = Reviews.map((review) => {
      const { review_file, user, parcel_service, rental, ...rest } = review;

      // 리뷰 파일 추출
      const files = review_file.length
        ? review_file.map((file) => ({
            ...file,
            file_url: `${STORE_URL}/${file.file_url}`,
          }))
        : [];

      return {
        ...rest,
        review_img: files,
        product_name: parcel_service?.product.product_name,
        product_option: parcel_service?.product_option,
        user_name: `${user.user_name?.[0]}**`,
      };
    });

    const newReviewList = reviewList.reduce(
      (acc, review) => {
        const { review_img } = review;

        if (review_img.length) {
          acc['photo'].push(review);
        }

        acc['reviews'].push(review);

        return acc;
      },
      { reviews: [], photo: [] },
    );
    const sortData = {
      reviews: newReviewList.reviews.sort(
        (a, b) => b.created_at - a.created_at,
      ),
      photo: newReviewList.photo.sort((a, b) => b.created_at - a.created_at),
    };

    return sortData;
  }, [Reviews]);

  /**
   * 상품 옵션 정제
   * --
   */
  const ProductOptions = React.useMemo(() => {
    if (
      !ProductData.product ||
      !ProductData.product.product_option ||
      ProductData.product.product_option.length === 0
    ) {
      setSelectedOption(null);
      return null;
    }

    setSelectedOption([]);
    const newOptions = ProductData.product.product_option.map((option) => ({
      key: `${option.option_id}`,
      title: option.option_title,
      type: option.type,
      children: option.option.map((op) => ({
        label: `${op.option_name}${
          op.option_price ? ` (+${stringToMoneyFormat(op.option_price)})` : ''
        }`,
        value: `${option.option_id}-${op.option_key}`,
      })),
      origin: option,
    }));

    const groupedOptions = newOptions.reduce(
      (acc, cur) => {
        if (acc[cur.type]) {
          acc[cur.type].push(cur);
        }
        return acc;
      },
      { REQUIRED: [], ADDITION: [] },
    );

    // 각 그룹별로 정렬
    groupedOptions.REQUIRED.sort((a, b) => a.order - b.order);
    groupedOptions.ADDITION.sort((a, b) => a.order - b.order);
    return groupedOptions;
  }, [ProductData.product]);

  // Tab Item
  const tabItems = React.useMemo(() => {
    return [
      {
        key: 'content',
        label: '상품설명',
      },
      {
        key: 'detail',
        label: '상세정보',
      },
      {
        key: 'review',
        label: `리뷰 (${
          (Reviews?.length ?? 0) > 999 ? '+999' : (Reviews?.length ?? 0)
        })`,
      },
      {
        key: 'inquiry',
        label: '문의',
      },
    ];
  }, [Reviews]);

  /**
   * 옵션이 없는 상품일 경우
   * --
   */
  React.useEffect(() => {
    if (ProductData && ProductData.product) {
      const { product_option, finalPrice, type } = ProductData.product;

      // 옵션이 없는 상품일 경우 바로 선택된 상품에 담기
      if (
        (!product_option || !product_option.length) &&
        type !== 'SUBSCRIPTION'
      ) {
        setSelectedProduct([
          { ...ProductData.product, cartNum: 1, totalPrice: finalPrice },
        ]);
      } else {
        setSelectedProduct([]);
      }
    }
  }, [ProductData]);

  /**
   * 위시리스트 여부
   * --
   */
  const isWish = React.useMemo(() => {
    return wishlist.some(
      (wish) => wish.product_id === ProductIdx && wish.type === 'WISH',
    );
  }, [wishlist, ProductIdx]);

  /**
   * 재입고 알림 신청 여부
   * --
   */
  const isRestock = React.useMemo(() => {
    return wishlist.some(
      (wish) => wish.product_id === ProductIdx && wish.type === 'RESTOCK',
    );
  }, [wishlist, ProductIdx]);

  /**
   * 다운로드 가능 쿠폰 추출
   * --
   */
  const DownloadCoupon = React.useMemo(() => {
    if (!Coupons.length) return null;

    const TODAY = dayjs().unix();

    // 다운로드 가능한 쿠폰 추출
    const filterCoupons = Coupons.filter((coupon) => {
      const {
        is_show,
        coupon_issue_num,
        coupon_use_num,
        start_date,
        end_date,
        map_product_policy,
      } = coupon;

      const isVisible = is_show === 'Y';
      const isOverIssued =
        coupon_issue_num === 0 ||
        (coupon_issue_num > 0 && coupon_use_num < coupon_issue_num);
      const isStarted = TODAY > (start_date ?? 0);
      const isNotExpired = TODAY < (end_date ?? Infinity);
      const isInclude = map_product_policy.some(
        (policy) => policy.product_id === ProductIdx,
      );

      return (
        isVisible && isOverIssued && isStarted && isNotExpired && isInclude
      );
    });

    // 다운로드를 한 쿠폰일 경우 Y
    const newCoupon = filterCoupons.map((coupon) => ({
      ...coupon,
      isDownload: !userInfo
        ? 'N'
        : couponList.some(
              (storage) =>
                coupon.coupon_id === storage.coupon_id &&
                ((coupon.is_reissue === 'Y' && !storage.use_date) ||
                  coupon.is_reissue === 'N'),
            )
          ? 'Y'
          : 'N',
    }));

    return newCoupon.length ? newCoupon : null;
  }, [Coupons, ProductIdx, couponList, userInfo]);

  /**
   * 스크롤 이벤트 등록
   * --
   */
  React.useEffect(() => {
    const call = () => {
      if (!contentRef) return;

      const heights = Object.entries(contentRef)
        .map(([key, value]) => {
          if (key === 'current' || !value) return null;
          return {
            [key]: value.getBoundingClientRect().y - 283,
          };
        })
        .filter(Boolean)
        .sort((a, b) => Object.values(a)[0] - Object.values(b)[0]);

      const findHeight = heights
        .filter((con) => Object.values(con)[0] < 0)
        .pop();

      const findKey = findHeight ? Object.keys(findHeight)?.[0] : 'content';
      setTabKey(findKey);
    };

    // 리스너 등록 (contentHeight를 클로저로 전달)
    if (contentRef.content) {
      // console.log('재등록');
      window.addEventListener('scroll', call);
    }

    // 리스너 해제
    return () => {
      window.removeEventListener('scroll', call);
    };
  }, [contentRef, isMore, agentType]);

  /**
   * 렌탈 기본 선택 옵션 렌더링
   * --
   */
  React.useEffect(() => {
    if (!ProductData.product || ProductData.product?.type !== 'RENTAL') return;

    const { product } = ProductData;
    setSelectedRentalOption(product?.default_rental_option);
    // eslint-disable-next-line
  }, [ProductData.product]);

  /**
   * 최근 본 상품
   * --
   */
  React.useEffect(() => {
    // sessionStorage에서 기존 데이터 가져오기
    const getStorage = sessionStorage.getItem('recent-list');

    const { product } = ProductData;
    if (!product) return; // product가 없으면 실행하지 않음

    // 현재 보고 있는 상품의 데이터 객체 생성
    const newData = {
      key: product?.product_id,
      thumbnail: product?.src,
      title: product?.product_name,
    };

    let recentList = [];

    if (getStorage) {
      try {
        recentList = JSON.parse(getStorage) || [];
      } catch (error) {
        console.error('sessionStorage parsing error:', error);
        recentList = [];
      }
    }

    // 같은 key를 가진 상품이 있다면 제거
    recentList = recentList.filter((item) => item.key !== newData.key);

    // 새로운 상품을 가장 앞에 추가
    recentList.unshift(newData);

    // 최근 본 상품 개수 제한
    if (recentList.length > 10) {
      recentList = recentList.slice(0, 10);
    }

    // sessionStorage에 업데이트된 리스트 저장
    sessionStorage.setItem('recent-list', JSON.stringify(recentList));
  }, [ProductIdx, ProductData]);

  /**
   * 디바이스 타입 설정
   * --
   */
  React.useEffect(() => {
    const mobileCheck = isMobile();

    if (!mobileCheck) return;

    const isNative = window.ReactNativeWebView;

    if (isNative) {
      setAgentType('WEBVIEW');
    } else {
      setAgentType('MOBILE');
    }
  }, []);

  /* ===== RENDER ===== */
  return (
    <>
      <ProductDetailPresenter
        layoutRef={layoutRef}
        contentRef={contentRef}
        //
        user={userInfo}
        tabItems={tabItems}
        tabKey={tabKey}
        product={ProductData.product}
        information={ProductData.information}
        reviews={ReviewList}
        inquiries={InquiryList}
        selectedProduct={selectedProduct}
        productOptions={ProductOptions}
        selectedOption={selectedOption}
        isWish={isWish}
        isRestock={isRestock}
        isMore={isMore}
        regularCycle={regularCycle}
        inputValue={inputValue}
        files={files}
        isLoading={isLoading}
        maxFileCount={MAX_FILE_COUNT}
        maxFileSize={MAX_IMAGE_SIZE}
        downloadCoupon={DownloadCoupon}
        agentType={agentType}
        //
        setRegularCycle={setRegularCycle}
        onTabKey={handleTabChange}
        navigate={navigate}
        onProductList={handleProductList}
        onSubmit={handleSubmit}
        onOptionSelect={handleOptionSelect}
        setIsMore={setIsMore}
        onReception={handleReceptionInquiry}
        onChange={handleChange}
        onFileList={handleFileList}
        onCopyUrl={handleCopyUrl}
        onCouponDownload={handleCouponDownload}
        location={location}
        selectedRentalOption={selectedRentalOption}
        onSelectRentalOption={handleSelectRentalOption}
        calculateTotalOptionPrice={calculateTotalOptionPrice}
        screenSize={screenSize}
      />
    </>
  );
};

export default ProductDetailContainer;
